<script>
  // src/MapScripting.svelte
  import { onMount, onDestroy, createEventDispatcher, tick } from "svelte";
  import { fade, fly, slide } from "svelte/transition";
  const sheetDuration = 220;
  const dropdownDuration = 120;
  /** Vertical gap between cursor/anchor point and bottom of the floating label (px). */
  const FLOATING_LABEL_OFFSET_Y = 16;
  const labelOpenDuration = 420;
  const labelEaseOut = (t) => 1 - Math.pow(1 - t, 3);
  import {
    addPOIs,
    removePois,
    addNumericPOIs,
    removeNumericPois,
    addHiddenPoiToMap,
    removeHiddenPoiFromMap,
    addHiddenNumericPoiToMap,
    removeHiddenNumericPoiFromMap,
  } from "./mapPois";
  import { addPolygonPOIs, removePolygonPOIs } from "./mapPolygonPois";
  import { addBuildings, addStandardBuildings, setBasemapLandmarks, resolveStandardBuildingCenter, syncBuildingLabelLayer, buildingLabelPointFeaturesStandard, buildingLabelPointFeaturesTileset, refreshBuildingLabelFilters } from "./mapBuildings";
  import { addVisualLayer } from "./mapVisualLayer";
  import { addMapRasterOverlays, removeAllMapRasterOverlays } from "./mapRasterOverlays.js";
  import { addImageTourLayers, removeImageTourLayers } from "./mapImageTourLayers.js";
  import { normalizedToLeafletLatLng } from "./mapImageCoords.js";
  import { getDirectionsCoordsForPopup } from "./mapDirectionsHelpers.js";
  import L from "leaflet";
  import { getOrderedMapbox } from "./mapMenuOrder.js";
  // import { createFilterFromFeatures } from "./mapFilter";
  import MapLocations from "./MapLocations.svelte";
  import Icon from "@iconify/svelte";
  import PopupGallery from "./PopupGallery.svelte";
  import MapFloatingInlineGallery from "./MapFloatingInlineGallery.svelte";
  import PopupNumericPoi from "./PopupNumericPoi.svelte";
  import PopupMap360 from "./PopupMap360.svelte";
  import { focusTrap } from "./utils.js";
  import { tourFunctions } from "../tour-functions";

  import UA from "ua-parser-js";
  const ua = new UA();
  let isMobile = ua.getResult().device["type"] == "mobile";
  const osName = ua.getResult().os?.name || "";

  /** Tour-wide numeric POI active display style. */
  function normalizeNumericPoiStyle(value) {
    if (value === "inline") return "inline";
    if (value === "hotspot") return "hotspot";
    return "icon";
  }

  function isNumericHotspotStyle() {
    return normalizeNumericPoiStyle(mapbox?.numericPoiStyle) === "hotspot";
  }

  let numericHotspotPopup = null;
  let map360Popup = null;
  let numericPoiNavTimeout = null;
  let numericPoiFlyEndHandler = null;

  function cancelNumericPoiNav() {
    if (numericPoiNavTimeout != null) {
      clearTimeout(numericPoiNavTimeout);
      numericPoiNavTimeout = null;
    }
    if (map && numericPoiFlyEndHandler) {
      map.off("moveend", numericPoiFlyEndHandler);
      numericPoiFlyEndHandler = null;
    }
  }

  function destroyMap360Popup() {
    if (map360Popup?.$destroy) {
      map360Popup.$destroy();
      map360Popup = null;
    }
    numericHotspotPopup?.$set({ obscured: false });
  }

  function destroyNumericHotspotPopup() {
    cancelNumericPoiNav();
    destroyMap360Popup();
    if (numericHotspotPopup?.$destroy) {
      numericHotspotPopup.$destroy();
      numericHotspotPopup = null;
    }
  }

  function getVisibleNumericPois() {
    return (mapbox?.numericPois ?? []).filter((poi) => !poi?.hidden);
  }

  function numericPoiItemFromData(poiData) {
    if (!poiData) return null;
    return {
      type: "numericPoi",
      title: poiData.title,
      data: {
        ...poiData,
        location: parseLocation(poiData.location) ?? poiData.location,
      },
    };
  }

  function getNumericPoiSequenceIndex(poiId) {
    if (poiId == null) return -1;
    const id = String(poiId);
    return getVisibleNumericPois().findIndex((poi) => String(poi.id) === id);
  }

  function flyMapToLngLat(lngLat) {
    return new Promise((resolve) => {
      if (!map || !lngLat) {
        resolve();
        return;
      }

      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        if (numericPoiFlyEndHandler) {
          map.off("moveend", numericPoiFlyEndHandler);
          numericPoiFlyEndHandler = null;
        }
        resolve();
      };

      const fallback = setTimeout(finish, 1400);
      const onEnd = () => {
        clearTimeout(fallback);
        finish();
      };

      numericPoiFlyEndHandler = onEnd;
      map.once("moveend", onEnd);

      try {
        if (map._isLeafletImageMap) {
          map.flyTo(lngLat, Math.max(map.getZoom(), 1), { animate: true, duration: 0.5 });
        } else {
          map.flyTo({
            center: lngLat,
            padding: { top: 40, bottom: 180, left: 40, right: 40 },
            duration: 500,
            essential: true,
          });
        }
      } catch (_) {
        clearTimeout(fallback);
        finish();
      }
    });
  }

  async function navigateToNumericPoiAtIndex(targetIndex) {
    if (!map) return;
    const visible = getVisibleNumericPois();
    if (targetIndex < 0 || targetIndex >= visible.length) return;

    const poi = visible[targetIndex];
    const item = numericPoiItemFromData(poi);
    if (!item) return;

    destroyNumericHotspotPopup();
    highlightItem(item, { skipHotspotPopup: true, fromMapTap: false });

    const lngLat = getItemLngLat(item);
    await flyMapToLngLat(lngLat);

    numericPoiNavTimeout = setTimeout(() => {
      numericPoiNavTimeout = null;
      const galleryImages = mapGalleryImagesForDisplay(poi.galleryImages ?? []);
      if (isNumericHotspotStyle()) {
        openNumericHotspotPopup(item, galleryImages);
      }
    }, 100);
  }

  async function navigateAdjacentNumericPoi(direction) {
    const currentIndex = getNumericPoiSequenceIndex(selectedItem?.data?.id);
    if (currentIndex < 0) return;
    await navigateToNumericPoiAtIndex(currentIndex + direction);
  }

  function restartNumericPoiTour() {
    return navigateToNumericPoiAtIndex(0);
  }

  function openMap360Popup(scenes) {
    if (!scenes?.length) return;
    destroyMap360Popup();
    numericHotspotPopup?.$set({ obscured: true });
    const target = document.getElementById("popup-container") ?? document.body;
    map360Popup = new PopupMap360({
      target,
      props: {
        tour,
        linkedScenes: scenes,
        onClose: destroyMap360Popup,
      },
    });
  }

  function openNumericHotspotPopup(item, galleryImages) {
    if (!item?.data) return;
    destroyNumericHotspotPopup();
    const data = item.data;
    const lngLat = getDirectionsCoordsForPopup(data, tour?.mapbox?.mapDisplayMode);
    const directionsUrls = lngLat ? getDirectionsUrls(lngLat) : null;
    const rawDesc = typeof data.description === "string" ? data.description.trim() : "";
    const parsedContent = rawDesc ? tourFunctions.parseContent(rawDesc) : "";
    const scenes = (() => {
      if (!data.sceneIds || !tour?.scenes) return [];
      let sceneIds = data.sceneIds;
      if (typeof sceneIds === "string") {
        try {
          sceneIds = JSON.parse(sceneIds);
        } catch (_) {
          return [];
        }
      }
      const list = Array.isArray(sceneIds) ? sceneIds : [];
      return list
        .map((sceneId) => {
          const scene = tour.scenes.find((s) => s.id === sceneId);
          return scene ? { id: sceneId, name: scene.name, title: scene.title } : null;
        })
        .filter(Boolean);
    })();
    const target = document.getElementById("popup-container") ?? document.body;
    const sequenceIndex = getNumericPoiSequenceIndex(data.id);
    const visiblePois = getVisibleNumericPois();
    const visibleCount = visiblePois.length;
    const hasNext = sequenceIndex >= 0 && sequenceIndex < visibleCount - 1;
    const showRestart = visibleCount > 1 && sequenceIndex === visibleCount - 1;
    const nextPoi = hasNext ? visiblePois[sequenceIndex + 1] : null;
    const numericBg = mapbox?.numericPoiBgColor || tour?.theme?.primaryColor || "#000000";
    const numericText = mapbox?.numericPoiTextColor || "#fff";
    numericHotspotPopup = new PopupNumericPoi({
      target,
      props: {
        title: data.title ?? "",
        content: parsedContent,
        galleryImages,
        directionsUrls,
        linkedScenes: scenes,
        hasNextLocation: hasNext,
        showRestart,
        onRestart: () => restartNumericPoiTour(),
        nextStopNumber: hasNext ? sequenceIndex + 2 : null,
        nextStopPoi: nextPoi,
        nextStopBadgeBg: numericBg,
        nextStopBadgeText: numericText,
        onNextLocation: () => navigateAdjacentNumericPoi(1),
        onClose: () => highlightItem(null),
        onOpen360: () => openMap360Popup(scenes),
      },
    });
  }

  /** Get directions URLs for Google Maps, Apple Maps, and Waze. Pass real-world [lng, lat] only. */
  function getDirectionsUrls(lngLat) {
    if (!lngLat || lngLat.length < 2) return null;
    const lat = lngLat[1];
    const lng = lngLat[0];
    const coords = `${lat},${lng}`;
    const travelMode = "data=!3m1!4b1!4m2!4m1!3e2"; // walking
    return {
      google: `https://www.google.com/maps/dir/${coords}/@${coords},12z/${travelMode}`,
      apple: `https://maps.apple.com/?daddr=${coords}&dirflg=w`,
      waze: `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`,
    };
  }
  // Exports
  export let tour;
  export let expanded;
  export let map; // Map instance passed as prop
  /** When set (e.g. from Tour via Mapbox), called to switch from map to virtual tour. */
  export let switchToTour = null;
  /** When true (mobile), show the Locations slide-up sheet. */
  export let mobileLocationsOpen = false;
  /** Callback to close the mobile Locations sheet. */
  export let onCloseMobileLocations = () => {};
  /** When true, focus the locations filter input (e.g. opened via Search button). */
  export let focusLocationsFilterOnOpen = false;
  /** Called after focusing the filter so parent can clear the flag. */
  export let onFocusLocationsFilterDone = () => {};
  /** Show desktop/mobile locations menu UI. */
  export let showLocationsMenu = true;

  let mapMeasurementActive = false;
  function setMapMeasurementActive(active) {
    mapMeasurementActive = active;
  }

  // Mobile sheet: size to visual viewport so keyboard doesn't push content out of view
  const BOTTOM_BAR_HEIGHT = 72;
  let mobileSheetTop = 0;
  let mobileSheetHeight = 400;

  function updateMobileSheetViewport() {
    if (typeof window === 'undefined' || !window.visualViewport) return;
    const vp = window.visualViewport;
    mobileSheetTop = vp.offsetTop;
    // When keyboard is open (visual viewport much smaller), use full height so no gap remains
    const keyboardLikelyOpen = vp.height < (window.innerHeight * 0.75);
    mobileSheetHeight = keyboardLikelyOpen
      ? Math.max(200, vp.height)
      : Math.max(200, vp.height - BOTTOM_BAR_HEIGHT);
  }

  $: if (expanded && isMobile && mobileLocationsOpen) {
    updateMobileSheetViewport();
  }

  onMount(() => {
    const vp = typeof window !== 'undefined' && window.visualViewport;
    if (!vp) return;
    vp.addEventListener('resize', updateMobileSheetViewport);
    vp.addEventListener('scroll', updateMobileSheetViewport);
    return () => {
      vp.removeEventListener('resize', updateMobileSheetViewport);
      vp.removeEventListener('scroll', updateMobileSheetViewport);
    };
  });

  // Mapbox data with global Map Menu order applied (from mapCategories when present)
  $: mapbox = tour?.mapbox ? getOrderedMapbox(tour.mapbox) : null;

  let tooltipX = 0;
  let tooltipY = 0;
  let tooltipText;
  let tooltipOffsetY = FLOATING_LABEL_OFFSET_Y;
  /** Bumped on deselect to tear down in-flight label transitions before hover can remount. */
  let labelGeneration = 0;
  /** Label shown when a location is selected from the map menu: { text, lngLat } */
  let selectedLabel = null;
  /** Full selected item (from menu or map click) for popup: { type, title, data } */
  let selectedItem = null;
  let mapMoveCounter = 0;
  let mapMoveHandler = null;
  let mapMoveEndHandler = null;
  /** Increment to tell MapLocations to clear its selection (e.g. after click on empty map). */
  let mapMenuClearTrigger = 0;
  let layersAdded = false;
  let poisAdded = false;
  let styleLoaded = false; // Track when map style is loaded
  
  function addInitialLayers() {
    if (layersAdded || !map) return;
    if (map._isLeafletImageMap) {
      layersAdded = true;
      return;
    }
    layersAdded = true;
    
    if (mapbox?.visualFeatures?.length > 0) {
      const { visualFeatures, visualFeaturesColor } = mapbox;
      const layers = map.getStyle().layers;
      const desiredLayer = "waterway";
      const beforeLayerId = layers.find((l) => l.id === desiredLayer)?.id;

      addVisualLayer({
        map,
        beforeLayerId,
        visualFeatures,
        visualFeaturesColor,
      });
    }

    const mapDisplayMode = mapbox?.mapDisplayMode ?? "mapbox";
    if (mapDisplayMode !== "image" && mapbox?.mapRasterOverlays?.length > 0) {
      const ro = mapbox.mapRasterOverlays;
      const ids = ro.map((o) => o.id).filter(Boolean);
      removeAllMapRasterOverlays(map, ids);
      addMapRasterOverlays({ map, overlays: ro });
    }

    // Independent of tour buildings: toggle the basemap's 3D landmark buildings.
    setBasemapLandmarks(map, mapbox?.showLandmarks ?? true);

    if (mapbox?.buildings?.length > 0 || mapbox?.showOnlyAddedBuildings) {
      const {
        mapboxTilesetUrl: tilesetUrl,
        mapboxTilesetLayerName: tilesetLayerName,
        labelsUrl,
        labelsLayerName,
        buildingColor,
        buildingHoverColor,
        buildings,
        showOnlyAddedBuildings,
        showLandmarks,
        showBuildingLabels,
      } = mapbox;

      // Legacy tileset buildings vs. Mapbox Standard basemap buildings (source === "mapbox").
      const tilesetBuildings = (buildings || []).filter((b) => b.source !== "mapbox");
      const standardBuildings = (buildings || []).filter((b) => b.source === "mapbox");

      if (tilesetBuildings.length > 0 && tilesetUrl) {
        addBuildings({
          map,
          tilesetUrl,
          tilesetLayerName,
          labelsUrl,
          labelsLayerName,
          buildingColor,
          buildings: tilesetBuildings,
          buildingHeight: 7,
          buildingHoverColor,
          setTooltip,
          scenes: tour?.scenes,
          onBuildingClick: handleMapBuildingClick,
          showOnlyAddedBuildings: showOnlyAddedBuildings ?? false,
          showBuildingLabels: (showBuildingLabels ?? false) && expanded,
        });
      }

      if (standardBuildings.length > 0 || showOnlyAddedBuildings) {
        addStandardBuildings({
          map,
          buildings: standardBuildings,
          buildingColor,
          buildingHoverColor,
          setTooltip,
          scenes: tour?.scenes,
          onBuildingClick: handleMapBuildingClick,
          showOnlyAddedBuildings: showOnlyAddedBuildings ?? false,
          showLandmarks: showLandmarks ?? true,
          showBuildingLabels: (showBuildingLabels ?? false) && expanded,
        });
      }
    }
  }

  /** Building title labels only on the expanded map (hidden on the corner mini map). */
  function syncBuildingLabelsForExpanded() {
    if (!map || map._isLeafletImageMap || !layersAdded) return;

    const show = (mapbox?.showBuildingLabels ?? false) && expanded;
    const buildings = mapbox?.buildings || [];
    const tilesetLayerName = mapbox?.mapboxTilesetLayerName;
    const tilesetUrl = mapbox?.mapboxTilesetUrl;

    const standardBuildings = buildings.filter(
      (b) => b.source === "mapbox" && !b.hidden && b.geometry,
    );
    syncBuildingLabelLayer(
      map,
      show ? buildingLabelPointFeaturesStandard(standardBuildings) : [],
      {
        sourceId: "standard-tour-buildings-labels-src",
        layerId: "standard-tour-buildings-labels-layer",
      },
    );

    const tilesetBuildings = buildings.filter((b) => b.source !== "mapbox");
    if (tilesetUrl && tilesetLayerName) {
      syncBuildingLabelLayer(
        map,
        show ? buildingLabelPointFeaturesTileset(map, tilesetBuildings, tilesetLayerName) : [],
        { sourceId: "tour-buildings-labels-src", layerId: "tour-buildings-labels-layer" },
      );
    }
    refreshBuildingLabelFilters(map);
  }
  
  function addPOIsToMap() {
    if (!map || !expanded || !styleLoaded || poisAdded) return;
    if (map._isLeafletImageMap) {
      poisAdded = true;
      refreshLeafletImageLayers();
      return;
    }
    
    poisAdded = true;
    
    if (mapbox?.pois?.length > 0) {
      const { pois, poiBgColor, poiTextColor, poiHoverColor } = mapbox;
      const bgColor = poiBgColor || tour?.theme?.primaryColor;
      const textColor = poiTextColor || "#fff";

      // Handle async addPOIs call
      (async () => {
        try {
          await addPOIs({
            map,
            setTooltip,
            pois,
            bgColor,
            textColor,
            hoverColor: poiHoverColor,
            scenes: tour?.scenes,
            slug: tour?.slug,
            onPoiClick: handleMapPoiClick,
            mapDisplayMode: mapbox?.mapDisplayMode,
          });
        } catch (error) {
          console.error('Failed to add POIs:', error);
        }
      })();
    }

    if (mapbox?.numericPois?.length > 0) {
      const { numericPois, numericPoiBgColor, numericPoiTextColor, numericPoiHoverColor } = mapbox;
      const bgColor = numericPoiBgColor || tour?.theme?.primaryColor;
      const textColor = numericPoiTextColor || "#fff";
      (async () => {
        try {
          await addNumericPOIs({
            map,
            setTooltip,
            pois: numericPois,
            bgColor,
            textColor,
            hoverColor: numericPoiHoverColor,
            scenes: tour?.scenes,
            onNumericPoiClick: handleMapNumericPoiClick,
            mapDisplayMode: mapbox?.mapDisplayMode,
          });
        } catch (error) {
          console.error("Failed to add numeric POIs:", error);
        }
      })();
    }

    if (mapbox?.polygonPois?.length > 0) {
      const { polygonPois, polygonPoiBgColor, polygonPoiTextColor } = mapbox;
      const bgColor = polygonPoiBgColor || tour?.theme?.primaryColor;
      const textColor = polygonPoiTextColor || "#fff";
      
      // Handle async addPolygonPOIs call
      (async () => {
        try {
      await addPolygonPOIs({
        map,
        setTooltip,
        polygonPois,
        bgColor,
        textColor,
        scenes: tour?.scenes,
        slug: tour?.slug,
        onPolygonPoiClick: handleMapPolygonPoiClick,
        mapDisplayMode: mapbox?.mapDisplayMode,
      });
        } catch (error) {
          console.error('Failed to add polygon POIs:', error);
        }
      })();
    }
  }

  const dispatch = createEventDispatcher();

  /** Parse location to [lng, lat] (handles string from API/serialization). */
  function parseLocation(loc) {
    if (Array.isArray(loc) && loc.length >= 2) return loc;
    if (typeof loc === "string") {
      try {
        const parsed = JSON.parse(loc);
        return Array.isArray(parsed) && parsed.length >= 2 ? parsed : null;
      } catch (_) {
        return null;
      }
    }
    return null;
  }

  /** Compute center for a menu item: Mapbox [lng,lat] or Leaflet LatLng for image maps. */
  function getItemLngLat(item) {
    if (!item?.data || !map) return null;
    if (map._isLeafletImageMap) {
      if (item.type === "polygonPoi" && item.data.coordinates != null) {
        let c = item.data.coordinates;
        if (typeof c === "string") {
          try {
            c = JSON.parse(c);
          } catch (_) {
            c = null;
          }
        }
        if (Array.isArray(c) && c.length >= 3) {
          let nx = 0,
            ny = 0,
            n = 0;
          c.forEach((p) => {
            if (Array.isArray(p) && p.length >= 2) {
              nx += p[0];
              ny += p[1];
              n++;
            }
          });
          return n
            ? normalizedToLeafletLatLng(L, nx / n, ny / n, map._imageAspectWOverH ?? 1)
            : null;
        }
      }
      const loc = parseLocation(item.data.location);
      if (loc)
        return normalizedToLeafletLatLng(L, loc[0], loc[1], map._imageAspectWOverH ?? 1);
      return null;
    }
    if (item.type === "building" && item.data.featureId) {
      // Mapbox Standard basemap buildings: stored center or geometry centroid.
      if (item.data.source === "mapbox") {
        return resolveStandardBuildingCenter(item.data);
      }
      try {
        const features = map.querySourceFeatures("base-buildings", {
          sourceLayer: mapbox?.mapboxTilesetLayerName,
          filter: ["==", ["get", "@id"], item.data.featureId],
        });
        if (features?.length > 0) {
          const coords = features[0].geometry.coordinates[0];
          const center = coords.reduce(
            (acc, c) => [acc[0] + c[0], acc[1] + c[1]],
            [0, 0]
          ).map((s) => s / coords.length);
          return center;
        }
      } catch (_) {}
      return null;
    }
    if (item.type === "polygonPoi" && item.data.coordinates != null) {
      let c = item.data.coordinates;
      if (typeof c === "string") {
        try {
          c = JSON.parse(c);
        } catch (_) {
          c = null;
        }
      }
      if (Array.isArray(c) && c.length >= 3) {
        let lng = 0,
          lat = 0,
          n = 0;
        c.forEach((p) => {
          if (Array.isArray(p) && p.length >= 2) {
            lng += p[0];
            lat += p[1];
            n++;
          }
        });
        return n ? [lng / n, lat / n] : null;
      }
    }
    const loc = parseLocation(item.data.location);
    return loc;
  }

  /** Highlight the building or polygon POI on the map (same as hover). Called when a map menu location is selected. Pass null to clear selection.
   * @param {object} [options] - Optional. Pass { fromMapTap: true } when selection came from a map tap.
   */
  function highlightItem(item, options) {
    if (!map) return;
    destroyNumericHotspotPopup();
    if (!map._isLeafletImageMap) {
      if (map.clearBuildingHighlight) map.clearBuildingHighlight();
      if (map.clearPolygonPoiHighlight) map.clearPolygonPoiHighlight();
      if (map.clearPoiHighlight) map.clearPoiHighlight();
      if (map.clearNumericPoiHighlight) map.clearNumericPoiHighlight();
      removeHiddenPoiFromMap(map);
      removeHiddenNumericPoiFromMap(map);
    }
    if (!item) {
      cancelNumericPoiNav();
      labelGeneration += 1;
      stackHeightCaptureToken++;
      selectionStackHeight = null;
      selectedLabel = null;
      selectedItem = null;
      selectionFromMenuOrList = false;
      pendingInitialLabelInView = false;
      resetSelectionUiState();
      setTooltip(null);
      return;
    }
    // Clear hover tooltip before selection state so hover/selection labels never overlap.
    tooltipX = 0;
    tooltipY = 0;
    tooltipText = false;
    tooltipOffsetY = FLOATING_LABEL_OFFSET_Y;
    selectionFromMenuOrList = !options?.fromMapTap;
    // Menu/list flyTo finishes on moveend — run one-shot in-view check then, not on every user pan.
    pendingInitialLabelInView = selectionFromMenuOrList;
    selectionStackHeight = null;
    selectedItem = item;
    const lngLat = getItemLngLat(item);
    const text = item?.title ?? "";
    selectedLabel = text && lngLat ? { text, lngLat } : null;
    let projectedPixel = null;
    try {
      if (lngLat && map) {
        if (map._isLeafletImageMap) {
          const p = map.latLngToContainerPoint(lngLat);
          projectedPixel = { x: p.x, y: p.y };
        } else {
          const p = map.project(lngLat);
          projectedPixel = { x: p.x, y: p.y };
        }
      }
    } catch (_) {}
    logMapLabel("poi select", {
      type: item.type,
      title: text,
      fromMapTap: !!options?.fromMapTap,
      selectionFromMenuOrList,
      lngLat,
      projectedPixel,
      containerSize: map.getContainer
        ? (() => {
            const r = map.getContainer().getBoundingClientRect();
            return { width: r.width, height: r.height, left: r.left, top: r.top };
          })()
        : null,
      isMobile,
    });
    resetSelectionUiState();
    scheduleSelectionStackHeightCapture();
    if (!map._isLeafletImageMap) {
      if (item.type === "building" && item.data?.featureId) {
        if (map.highlightBuilding) map.highlightBuilding(item.data.featureId);
      } else if (item.type === "polygonPoi" && item.data?.id != null) {
        if (map.highlightPolygonPoi) map.highlightPolygonPoi(item.data.id);
      } else if (item.type === "poi" && item.data) {
        if (item.data.hidden && mapbox?.pois?.length >= 0) {
          const { poiBgColor, poiTextColor, poiHoverColor } = mapbox;
          const bgColor = poiBgColor || tour?.theme?.primaryColor || "#000000";
          const textColor = poiTextColor || "#fff";
          addHiddenPoiToMap(map, item.data, { bgColor, textColor, hoverColor: poiHoverColor });
        }
        if (map.highlightPoi) map.highlightPoi(item.data);
      } else if (item.type === "numericPoi" && item.data?.id != null) {
        if (item.data.hidden && mapbox?.numericPois?.length >= 0) {
          const { numericPoiBgColor, numericPoiTextColor, numericPoiHoverColor } = mapbox;
          const bgColor = numericPoiBgColor || tour?.theme?.primaryColor || "#000000";
          const textColor = numericPoiTextColor || "#fff";
          void addHiddenNumericPoiToMap(map, item.data, undefined, { bgColor, textColor, hoverColor: numericPoiHoverColor });
        }
        if (map.highlightNumericPoi) map.highlightNumericPoi(item.data.id);
      }
    }

    if (item?.type === "numericPoi" && item.data && !options?.skipHotspotPopup) {
      const galleryImages = mapGalleryImagesForDisplay(item.data.galleryImages ?? []);
      if (isNumericHotspotStyle()) {
        tick().then(() => openNumericHotspotPopup(item, galleryImages));
      }
    }
    // Show description body immediately when a location is selected (user can still collapse via chevron).
    // contentExpanded = true;
  }

  $: if (expanded === false) {
    destroyNumericHotspotPopup();
    selectedLabel = null;
    selectedItem = null;
    open360DropdownOpen = false;
    directionsDropdownOpen = false;
  }

  $: if (selectedItem === null) {
    open360DropdownOpen = false;
    directionsDropdownOpen = false;
    contentExpanded = false;
  }

  /** Same key as MapLocations itemKey() so menu can highlight the selected item when selection is from map click. */
  $: selectedItemMenuKey = selectedItem
    ? `${selectedItem.type}-${selectedItem.data?.id ?? selectedItem.data?.featureId ?? selectedItem.data?.title ?? selectedItem.title}`
    : null;

  /** Parsed description content for the selected item (building popupText or POI description). */
  $: labelContent = (() => {
    if (!selectedItem?.data) return "";
    const raw = selectedItem.type === "building"
      ? selectedItem.data?.popupText
      : selectedItem.data?.description;
    if (typeof raw !== "string" || !raw.trim()) return "";
    return tourFunctions.parseContent(raw.trim());
  })();

  $: hasLabelContent = labelContent.length > 0;

  function mapGalleryImagesForDisplay(galleryImages) {
    if (!Array.isArray(galleryImages)) return [];
    return galleryImages
      .map((img) => ({
        src: img?.src ?? img?.url ?? "",
        alt: img?.alt ?? img?.altText ?? "",
      }))
      .filter((img) => img.src);
  }

  $: isNumericSelection = selectedItem?.type === "numericPoi";
  $: selectedGalleryImages = isNumericSelection
    ? mapGalleryImagesForDisplay(selectedItem?.data?.galleryImages ?? [])
    : [];
  $: hasGallery = selectedGalleryImages.length > 0;
  $: tourNumericPoiStyle = normalizeNumericPoiStyle(mapbox?.numericPoiStyle);
  $: activeNumericPoiStyle = isNumericSelection
    ? (tourNumericPoiStyle === "hotspot" ? "hotspot" : hasGallery ? tourNumericPoiStyle : null)
    : null;
  $: showInlineGallery = activeNumericPoiStyle === "inline";
  $: showIconGallery = activeNumericPoiStyle === "icon";
  $: showHotspotPopup = activeNumericPoiStyle === "hotspot";
  $: numericHasExpandableBlock = isNumericSelection && (hasLabelContent || hasGallery);
  $: hasExpandableLabelContent = hasLabelContent || (isNumericSelection && hasGallery);

  let contentExpanded = false;

  function resetSelectionUiState() {
    open360DropdownOpen = false;
    directionsDropdownOpen = false;
    const isNumeric = selectedItem?.type === "numericPoi";
    let hasDesc = false;
    if (selectedItem?.data) {
      const raw =
        selectedItem.type === "building"
          ? selectedItem.data?.popupText
          : selectedItem.data?.description;
      hasDesc = typeof raw === "string" && raw.trim().length > 0;
    }
    const gallery =
      isNumeric && selectedItem?.data
        ? mapGalleryImagesForDisplay(selectedItem.data.galleryImages ?? [])
        : [];
    contentExpanded = isNumeric ? hasDesc || gallery.length > 0 : false;
  }

  function openMapGallery() {
    const galleryElem = document.getElementById("popup-gallery");
    if (!galleryElem || !selectedGalleryImages.length) return;
    new PopupGallery({
      target: galleryElem,
      props: {
        gallery: selectedGalleryImages,
        close: () => {},
      },
    });
    focusTrap(galleryElem);
  }

  function toggleContentExpanded() {
    contentExpanded = !contentExpanded;
  }

  /** Linked scenes for the selected item (for Open 360 button). */
  function sceneTitleForDisplay(title) {
    return (title || "").replace(/\[br\]/g, " ");
  }

  $: linkedScenes = (() => {
    if (!selectedItem?.data?.sceneIds || !tour?.scenes) return [];
    let sceneIds = selectedItem.data.sceneIds;
    if (typeof sceneIds === "string") {
      try {
        sceneIds = JSON.parse(sceneIds);
      } catch (_) {
        return [];
      }
    }
    const list = Array.isArray(sceneIds) ? sceneIds : [];
    return list
      .map((sceneId) => {
        const scene = tour.scenes.find((s) => s.id === sceneId);
        return scene ? { id: sceneId, name: scene.name, title: scene.title } : null;
      })
      .filter(Boolean);
  })();

  let open360DropdownOpen = false;
  let directionsDropdownOpen = false;
  let floatingLabelEl = null;
  let floatingLabelExiting = false;
  /** Collapsed selection menu height; pins top edge so description expansion grows downward. */
  let selectionStackHeight = null;
  let stackHeightCaptureToken = 0;
  /** Mobile: top/bottom inset so centered label stack stays clear of chrome / controls. */
  const LABEL_VIEW_INSET_MOBILE = 72;
  const LABEL_VIEW_MARGIN_DESKTOP = 24;
  /** Desktop: clear logo / top chrome so labels are not clipped at the viewport top. */
  const LABEL_VIEW_TOP_DESKTOP = 96;
  /** Desktop MapLocations: panel 300 + right 20 + buffer — treat entire strip as OOB. */
  const LABEL_VIEW_MENU_RIGHT_DESKTOP = 340;
  /** Desktop tour/map mini preview (small-view): 300×200 + margins — bottom-left OOB. */
  const LABEL_VIEW_SWITCHER_WIDTH_DESKTOP = 340;
  const LABEL_VIEW_SWITCHER_HEIGHT_DESKTOP = 240;
  /** True when selection came from menu/list (not map tap); map may still be panning/zooming. */
  let selectionFromMenuOrList = false;
  /** One-shot: after menu flyTo, run ensureLabelInView once on the next moveend. */
  let pendingInitialLabelInView = false;
  let labelInViewRetryCount = 0;
  const LABEL_IN_VIEW_RETRY_MAX = 10;
  /** Set when we panBy from ensureLabelInView so that moveend does not consume pendingInitialLabelInView. */
  let skipNextMoveEndForLabelInView = false;

  /** Union of floating label and any open absolute dropdown menus (for in-view panning). */
  function getFloatingLabelBounds() {
    const base = floatingLabelEl?.getBoundingClientRect();
    if (!base) return null;
    let top = base.top;
    let left = base.left;
    let right = base.right;
    let bottom = base.bottom;
    if (floatingLabelEl) {
      floatingLabelEl.querySelectorAll(".map-open360-dropdown-container, .map-dropdown-wrapper > .map-dropdown-menu").forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.width === 0 && r.height === 0) return;
        top = Math.min(top, r.top);
        left = Math.min(left, r.left);
        right = Math.max(right, r.right);
        bottom = Math.max(bottom, r.bottom);
      });
    }
    return {
      top,
      left,
      right,
      bottom,
      width: right - left,
      height: bottom - top,
    };
  }

  /**
   * Desktop safe rectangle inside the map container (viewport coords).
   * Reserves the locations menu (full right strip) and tour mini-map (bottom-left).
   */
  function getDesktopLabelViewSafeArea(containerRect) {
    const edge = LABEL_VIEW_MARGIN_DESKTOP;
    let right = containerRect.right - edge;
    if (expanded && showLocationsMenu) {
      right = Math.min(right, containerRect.right - LABEL_VIEW_MENU_RIGHT_DESKTOP);
    }
    const switcher = expanded
      ? {
          left: containerRect.left,
          right: containerRect.left + LABEL_VIEW_SWITCHER_WIDTH_DESKTOP,
          top: containerRect.bottom - LABEL_VIEW_SWITCHER_HEIGHT_DESKTOP,
          bottom: containerRect.bottom,
        }
      : null;
    return {
      top: containerRect.top + LABEL_VIEW_TOP_DESKTOP,
      right,
      bottom: containerRect.bottom - edge,
      left: containerRect.left + edge,
      switcher,
    };
  }

  function initialLabelInViewMode() {
    return isMobile ? "center" : "bounds";
  }

  function logMapLabel(tag, payload) {
    console.log(`[map-label] ${tag}`, payload);
  }

  /** Pan map so label moves by (dx, dy) on screen (positive = right/down). */
  function panMapForLabel(dx, dy) {
    if ((!dx && !dy) || typeof map.panBy !== "function") {
      logMapLabel("panBy skipped", { dx, dy, hasPanBy: typeof map?.panBy === "function" });
      return;
    }
    skipNextMoveEndForLabelInView = true;
    logMapLabel("panBy", { dx, dy, mapPan: [-dx, -dy] });
    if (map._isLeafletImageMap) {
      map.panBy([-dx, -dy], { animate: true, duration: 0.3 });
    } else {
      map.panBy([-dx, -dy], { duration: 300 });
    }
  }

  /** Mobile: center POI anchor on screen X; fit label vertically with top/bottom insets. */
  function ensureMobileLabelInView(labelRect, containerRect) {
    if (!selectedLabelPixel) return;
    const inset = LABEL_VIEW_INSET_MOBILE;
    const minTop = containerRect.top + inset;
    const maxBottom = containerRect.bottom - inset;
    const targetX = containerRect.left + containerRect.width / 2;
    const anchorX = containerRect.left + selectedLabelPixel.x;
    const containerCy = (containerRect.top + containerRect.bottom) / 2;
    const labelCy = (labelRect.top + labelRect.bottom) / 2;

    // X: always put the POI anchor on the screen midline (label uses translate(-50%)).
    const dx = targetX - anchorX;
    let dy = containerCy - labelCy;

    const shiftedTop = labelRect.top + dy;
    const shiftedBottom = labelRect.bottom + dy;
    const availableHeight = maxBottom - minTop;
    let verticalFit = "centered";

    if (labelRect.height > availableHeight) {
      dy += minTop - shiftedTop;
      verticalFit = "pin-top";
    } else {
      if (shiftedTop < minTop) {
        dy += minTop - shiftedTop;
        verticalFit = "nudge-top";
      } else if (shiftedBottom > maxBottom) {
        dy -= shiftedBottom - maxBottom;
        verticalFit = "nudge-bottom";
      }
    }

    logMapLabel("ensureMobileLabelInView", {
      inset,
      minTop,
      maxBottom,
      availableHeight,
      verticalFit,
      anchorX,
      targetX,
      cssFixedCenterX: true,
      selectedLabelPixel,
      container: {
        left: containerRect.left,
        top: containerRect.top,
        right: containerRect.right,
        bottom: containerRect.bottom,
        width: containerRect.width,
        height: containerRect.height,
        cy: containerCy,
      },
      labelBefore: {
        left: labelRect.left,
        top: labelRect.top,
        right: labelRect.right,
        bottom: labelRect.bottom,
        width: labelRect.width,
        height: labelRect.height,
        cy: labelCy,
      },
      labelAfterExpected: {
        left: labelRect.left + dx,
        top: labelRect.top + dy,
        right: labelRect.right + dx,
        bottom: labelRect.bottom + dy,
        cy: labelCy + dy,
      },
      dx,
      dy,
      lngLat: selectedLabel?.lngLat ?? null,
    });

    panMapForLabel(dx, dy);
  }

  /** Desktop: overflow clamp against safe area (menu / switcher / top chrome). */
  function ensureDesktopLabelInView(labelRect, containerRect, mode) {
    const safe = getDesktopLabelViewSafeArea(containerRect);
    let dx = 0;
    let dy = 0;

    if (mode === "center") {
      const labelCx = (labelRect.left + labelRect.right) / 2;
      const labelCy = (labelRect.top + labelRect.bottom) / 2;
      const safeCx = (safe.left + safe.right) / 2;
      const safeCy = (safe.top + safe.bottom) / 2;
      dx = safeCx - labelCx;
      dy = safeCy - labelCy;
    }

    let shiftedLeft = labelRect.left + dx;
    let shiftedRight = labelRect.right + dx;
    let shiftedTop = labelRect.top + dy;
    let shiftedBottom = labelRect.bottom + dy;

    const leftOverflow = safe.left - shiftedLeft;
    const rightOverflow = shiftedRight - safe.right;
    const topOverflow = safe.top - shiftedTop;
    const bottomOverflow = shiftedBottom - safe.bottom;
    if (leftOverflow > 0) dx += leftOverflow;
    if (rightOverflow > 0) dx -= rightOverflow;

    const safeHeight = safe.bottom - safe.top;
    if (labelRect.height > safeHeight) {
      if (topOverflow > 0) dy += topOverflow;
    } else {
      if (topOverflow > 0) dy += topOverflow;
      if (bottomOverflow > 0) dy -= bottomOverflow;
    }

    shiftedLeft = labelRect.left + dx;
    shiftedRight = labelRect.right + dx;
    shiftedBottom = labelRect.bottom + dy;

    if (safe.switcher) {
      const sw = safe.switcher;
      const overlapsSwitcherX = shiftedRight > sw.left && shiftedLeft < sw.right;
      if (overlapsSwitcherX) {
        const switcherOverflow = shiftedBottom - sw.top;
        if (switcherOverflow > 0) dy -= switcherOverflow;
      }
    }

    logMapLabel("ensureDesktopLabelInView", {
      mode,
      safe,
      labelBefore: {
        left: labelRect.left,
        top: labelRect.top,
        right: labelRect.right,
        bottom: labelRect.bottom,
        width: labelRect.width,
        height: labelRect.height,
      },
      labelAfterExpected: {
        left: labelRect.left + dx,
        top: labelRect.top + dy,
        right: labelRect.right + dx,
        bottom: labelRect.bottom + dy,
      },
      dx,
      dy,
      selectedLabelPixel,
      lngLat: selectedLabel?.lngLat ?? null,
    });

    panMapForLabel(dx, dy);
  }

  /**
   * Keep the floating label fully visible.
   * Mobile: center + vertical fit. Desktop: safe-area bounds (mode "center" unused on desktop initial open).
   * @param {"center"|"bounds"} [mode="bounds"]
   */
  function ensureLabelInView(mode = "bounds") {
    if (!map || !selectedLabel || !selectedLabelPixel) {
      logMapLabel("ensureLabelInView early return", {
        mode,
        hasMap: !!map,
        hasSelectedLabel: !!selectedLabel,
        selectedLabelPixel,
      });
      return;
    }
    // Mobile inline gallery uses viewport-fixed layout; hotspot popup is fullscreen.
    if (isMobile && showInlineGallery) {
      logMapLabel("ensureLabelInView skip inline gallery", { mode });
      return;
    }
    if (showHotspotPopup) {
      logMapLabel("ensureLabelInView skip hotspot popup", { mode });
      return;
    }
    if (!floatingLabelEl) {
      if (labelInViewRetryCount < LABEL_IN_VIEW_RETRY_MAX) {
        labelInViewRetryCount += 1;
        logMapLabel("ensureLabelInView retry waiting for el", {
          mode,
          labelInViewRetryCount,
        });
        setTimeout(() => ensureLabelInView(mode), 80);
      }
      return;
    }
    labelInViewRetryCount = 0;
    tick().then(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          try {
            const container = map.getContainer();
            const labelRect = getFloatingLabelBounds() ?? floatingLabelEl.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            logMapLabel("floating label measured", {
              mode,
              isMobile,
              styleLeft: floatingLabelEl.style.left,
              styleTop: floatingLabelEl.style.top,
              selectedLabelPixel,
              lngLat: selectedLabel?.lngLat ?? null,
              labelRect: {
                left: labelRect.left,
                top: labelRect.top,
                right: labelRect.right,
                bottom: labelRect.bottom,
                width: labelRect.width,
                height: labelRect.height,
              },
              containerRect: {
                left: containerRect.left,
                top: containerRect.top,
                width: containerRect.width,
                height: containerRect.height,
              },
            });
            if (isMobile) {
              ensureMobileLabelInView(labelRect, containerRect);
            } else {
              ensureDesktopLabelInView(labelRect, containerRect, mode);
            }
          } catch (err) {
            logMapLabel("ensureLabelInView error", { err: String(err) });
          }
        });
      });
    });
  }

  let labelInViewSelectionKey = null;
  let labelInViewExpandKey = "";

  $: if (!selectedLabel) {
    labelInViewRetryCount = 0;
    pendingInitialLabelInView = false;
    labelInViewSelectionKey = null;
    labelInViewExpandKey = "";
  }

  /** One ensure per selection open; one more only when dropdown/description toggles after that. */
  $: if (selectedLabel) {
    const expandOpen = directionsDropdownOpen || open360DropdownOpen || contentExpanded;
    const expandKey = `${directionsDropdownOpen}|${open360DropdownOpen}|${contentExpanded}`;
    const selKey = selectedItemMenuKey;
    if (!selectionFromMenuOrList) {
      if (selKey !== labelInViewSelectionKey) {
        labelInViewSelectionKey = selKey;
        labelInViewExpandKey = expandKey;
        setTimeout(() => ensureLabelInView(initialLabelInViewMode()), expandOpen ? 220 : 80);
      } else if (expandKey !== labelInViewExpandKey) {
        labelInViewExpandKey = expandKey;
        if (expandOpen) {
          setTimeout(() => ensureLabelInView(initialLabelInViewMode()), 220);
        }
      }
    } else if (selKey !== labelInViewSelectionKey) {
      // Menu: initial pan comes from moveend; remember expand state for later toggles.
      labelInViewSelectionKey = selKey;
      labelInViewExpandKey = expandKey;
    } else if (expandKey !== labelInViewExpandKey) {
      labelInViewExpandKey = expandKey;
      if (expandOpen) {
        setTimeout(() => ensureLabelInView(initialLabelInViewMode()), 220);
      }
    }
  }

  function openScene(sceneName) {
    tourFunctions.loadScene(sceneName, "map_popup");
    if (window.CC?.switchView) window.CC.switchView();
    open360DropdownOpen = false;
    highlightItem(null);
  }

  function openDirections(url) {
    window.open(url, "_blank");
    directionsDropdownOpen = false;
  }

  function toggle360Dropdown() {
    open360DropdownOpen = !open360DropdownOpen;
    if (open360DropdownOpen) directionsDropdownOpen = false;
  }

  function toggleDirectionsDropdown() {
    directionsDropdownOpen = !directionsDropdownOpen;
    if (directionsDropdownOpen) open360DropdownOpen = false;
  }

  /** Callbacks: map click selects the item (shows tooltip) instead of opening popup. */
  function handleMapPoiClick(poiData) {
    if (!poiData) return;
    const data = { ...poiData, location: parseLocation(poiData.location) ?? poiData.location };
    highlightItem({ type: "poi", title: poiData.title, data }, { fromMapTap: true });
  }
  function handleMapBuildingClick(building) {
    if (!building) return;
    highlightItem({ type: "building", title: building.title, data: building }, { fromMapTap: true });
  }
  function handleMapNumericPoiClick(poiData) {
    if (!poiData) return;
    const data = { ...poiData, location: parseLocation(poiData.location) ?? poiData.location };
    highlightItem({ type: "numericPoi", title: poiData.title, data }, { fromMapTap: true });
  }
  function handleMapPolygonPoiClick(poiData) {
    if (!poiData) return;
    const data = { ...poiData };
    if (typeof data.coordinates === "string") {
      try {
        data.coordinates = JSON.parse(data.coordinates);
      } catch (_) {
        /* leave as-is */
      }
    }
    highlightItem({ type: "polygonPoi", title: data.title, data }, { fromMapTap: true });
  }

  function refreshLeafletImageLayers() {
    if (!map || !map._isLeafletImageMap || !mapbox) return;
    addImageTourLayers(map, {
      tour,
      pois: mapbox.pois || [],
      numericPois: mapbox.numericPois || [],
      polygonPois: mapbox.polygonPois || [],
      poiBgColor: mapbox.poiBgColor || tour?.theme?.primaryColor,
      poiTextColor: mapbox.poiTextColor,
      numericPoiBgColor: mapbox.numericPoiBgColor,
      numericPoiTextColor: mapbox.numericPoiTextColor,
      polygonPoiBgColor: mapbox.polygonPoiBgColor,
      polygonPoiTextColor: mapbox.polygonPoiTextColor,
      setTooltip,
      onPoiClick: handleMapPoiClick,
      onNumericPoiClick: handleMapNumericPoiClick,
      onPolygonPoiClick: handleMapPolygonPoiClick,
      scenes: tour?.scenes,
    });
  }

  $: if (expanded && map && map._isLeafletImageMap && mapbox) {
    refreshLeafletImageLayers();
  }

  function captureSelectionStackHeight() {
    if (!selectedLabel || !floatingLabelEl || selectionStackHeight != null) return;
    // Numeric POIs start fully expanded; top-anchor is not used for them.
    if (selectedItem?.type === "numericPoi") return;
    if (contentExpanded) return;
    selectionStackHeight = floatingLabelEl.offsetHeight;
  }

  function scheduleSelectionStackHeightCapture() {
    const token = ++stackHeightCaptureToken;
    tick().then(() => {
      requestAnimationFrame(() => {
        if (token !== stackHeightCaptureToken) return;
        captureSelectionStackHeight();
      });
    });
    setTimeout(() => {
      if (token !== stackHeightCaptureToken) return;
      captureSelectionStackHeight();
    }, labelOpenDuration + 40);
  }

  $: if (!selectedLabel) selectionStackHeight = null;

  $: selectedLabelPixel =
    map && selectedLabel && mapMoveCounter >= 0
      ? (() => {
          try {
            if (map._isLeafletImageMap && selectedLabel.lngLat) {
              const p = map.latLngToContainerPoint(selectedLabel.lngLat);
              return { x: p.x, y: p.y };
            }
            const p = map.project(selectedLabel.lngLat);
            return { x: p.x, y: p.y };
          } catch (_) {
            return null;
          }
        })()
      : null;

  // function drawFilterFeatures(features, color = "#ff0000") {
  //   const sourceName = `filter-${Math.random().toString(36).substring(7)}`;
  //   map.addSource(sourceName, {
  //     type: "geojson",
  //     data: {
  //       type: "FeatureCollection",
  //       features,
  //     },
  //   });
  //   map.addLayer({
  //     id: sourceName,
  //     type: "line",
  //     source: sourceName,
  //     paint: {
  //       "line-color": color,
  //       "line-width": 2,
  //     },
  //   });
  // }
  // const hasInclusionFilter = tour?.mapbox?.inclusionFilterFeatures?.length > 0;
  // const hasExclusionFilter = tour?.mapbox?.exclusionFilterFeatures?.length > 0;
  // const excludeLayers = [
  //   "clusters",
  //   "cluster-count",
  //   "unclustered-point",
  //   "tour-buildings-layer",
  //   "tour-bg-buildings-layer",
  //   "tour-buildings-layer-labels",
  //   "tour-bg-buildings-layer-labels",
  //   "visual-layer-labels",
  //   "visual-layer",
  // ];

  // function applyFilters() {
  //   let layers = map.getStyle().layers;
  //   if (hasInclusionFilter || hasExclusionFilter) {
  //     layers = layers.filter((layer) => !excludeLayers.includes(layer.id));
  //     layers.forEach((layer) => {
  //       if (hasInclusionFilter) {
  //         map.setFilter(
  //           layer.id,
  //           createFilterFromFeatures(tour?.mapbox?.inclusionFilterFeatures),
  //         );
  //       }
  //       if (hasExclusionFilter) {
  //         map.setFilter(
  //           layer.id,
  //           createFilterFromFeatures(
  //             tour?.mapbox?.exclusionFilterFeatures,
  //             false,
  //           ),
  //         );
  //       }
  //     });
  //   }
  // }

  // function highlightExcludedFeatures() {
  //   if (hasExclusionFilter) {
  //     const exclusionFilter = createFilterFromFeatures(
  //       tour?.mapbox?.exclusionFilterFeatures,
  //       false,
  //     );
  //     map.addLayer({
  //       id: "excluded-features",
  //       type: "fill",
  //       source: "composite",
  //       filter: exclusionFilter,
  //       paint: {
  //         "fill-color": "#ff0000",
  //         "fill-opacity": 0.5,
  //       },
  //     });
  //   }
  // }

  function setTooltip(tooltip) {
    if (selectedLabel) {
      tooltipX = 0;
      tooltipY = 0;
      tooltipText = false;
      tooltipOffsetY = FLOATING_LABEL_OFFSET_Y;
      return;
    }

    if (!tooltip) {
      tooltipX = 0;
      tooltipY = 0;
      tooltipText = false;
      tooltipOffsetY = FLOATING_LABEL_OFFSET_Y;
      return;
    }

    const { x, y, text, offsetY } = tooltip;
    tooltipX = x;
    tooltipY = y;
    tooltipText = text;
    tooltipOffsetY = offsetY ?? FLOATING_LABEL_OFFSET_Y;
  }

  onMount(async () => {
    if (!map) {
      console.warn('Map prop not available in MapScripting');
      return;
    }
    if (map._isLeafletImageMap) {
      styleLoaded = true;
      layersAdded = true;
      poisAdded = true;
      refreshLeafletImageLayers();
      mapMoveHandler = () => { mapMoveCounter += 1; };
      map.on("move", mapMoveHandler);
      mapMoveEndHandler = () => {
        if (skipNextMoveEndForLabelInView) {
          skipNextMoveEndForLabelInView = false;
          return;
        }
        if (pendingInitialLabelInView && selectedLabel) {
          pendingInitialLabelInView = false;
          setTimeout(() => ensureLabelInView(initialLabelInViewMode()), 50);
        }
      };
      map.on("moveend", mapMoveEndHandler);
      map.on("click", (e) => {
        const t = e?.originalEvent?.target;
        if (
          t &&
          typeof t.closest === "function" &&
          (t.closest(".maplocations-outer") ||
            t.closest(".map-measure-floating") ||
            t.closest(".mobile-locations-sheet") ||
            t.closest(".mobile-locations-backdrop") ||
            t.closest(".map-floating-label-wrapper") ||
            t.closest(".leaflet-control-container"))
        ) {
          return;
        }
        if (selectedLabel) {
          highlightItem(null);
          mapMenuClearTrigger += 1;
        }
      });
      return;
    }
    mapMoveHandler = () => { mapMoveCounter += 1; };
    map.on("move", mapMoveHandler);
    mapMoveEndHandler = () => {
      if (skipNextMoveEndForLabelInView) {
        skipNextMoveEndForLabelInView = false;
        return;
      }
      if (pendingInitialLabelInView && selectedLabel) {
        pendingInitialLabelInView = false;
        setTimeout(() => ensureLabelInView(initialLabelInViewMode()), 50);
      }
    };
    map.on("moveend", mapMoveEndHandler);

    // Set up event listeners
    map.on("zoom", () => {
      console.log("zoom", map.getZoom());
    });

    function ourLayer(id) {
      return (
        id === "tour-buildings-layer" ||
        id === "standard-tour-buildings-layer" ||
        id?.startsWith("polygon-pois-") ||
        id === "clusters" ||
        id?.startsWith("unclustered-point-")
      );
    }
    function clearSelectionIfEmptyAtPoint(point) {
      try {
        const features = map.queryRenderedFeatures(point);
        const hitOurs = features.filter((f) => ourLayer(f.layer?.id));
        if (hitOurs.length === 0 && selectedLabel) {
          highlightItem(null);
          mapMenuClearTrigger += 1;
        }
      } catch (_) {}
    }
    function getPointFromTouch(touchEvent) {
      if (!touchEvent.changedTouches?.length) return null;
      const t = touchEvent.changedTouches[0];
      const canvas = map.getCanvas();
      const rect = canvas.getBoundingClientRect();
      const x = t.clientX - rect.left;
      const y = t.clientY - rect.top;
      if (typeof x !== "number" || typeof y !== "number" || Number.isNaN(x) || Number.isNaN(y)) return null;
      return { x, y };
    }

    map.on("click", function (e) {
      if (map._mapboxTouchHandledUntil && Date.now() < map._mapboxTouchHandledUntil) return;
      if (mapMeasurementActive) return;
      // Click on empty map: clear menu selection and map highlight/label
      clearSelectionIfEmptyAtPoint(e.point);
    });

    map.getCanvas().addEventListener("touchend", function mapEmptyTouchEnd(e) {
      try {
        if (mapMeasurementActive) return;
        const point = getPointFromTouch(e);
        if (!point || typeof point.x !== "number" || typeof point.y !== "number") return;
        const px = Number(point.x);
        const py = Number(point.y);
        if (!Number.isFinite(px) || !Number.isFinite(py)) return;
        clearSelectionIfEmptyAtPoint({ x: px, y: py });
      } catch (_) {}
    });
    
    // Add layers when map style is loaded
    if (!layersAdded) {
      if (map.isStyleLoaded()) {
        addInitialLayers();
        styleLoaded = true;
      } else {
        map.once('style.load', () => {
          addInitialLayers();
          styleLoaded = true;
        });
      }
    } else {
      // Layers already added, check if style is loaded
      if (map.isStyleLoaded()) {
        styleLoaded = true;
      }
    }
  });
  
  // Watch for map style loading (reactive statement)
  $: if (map && !styleLoaded && !map._isLeafletImageMap) {
    if (map.isStyleLoaded()) {
      styleLoaded = true;
    } else {
      map.once('style.load', () => {
        styleLoaded = true;
      });
    }
  }
  
  // Reactive statement: when expanded changes or style loads, add or remove POIs
  $: if (expanded && map && styleLoaded) {
    // Add POIs when map is expanded and style is loaded
    if (!poisAdded) {
      addPOIsToMap();
    }
  } else if (map && !expanded) {
    // When collapsing, remove POIs and reset flag
    poisAdded = false;
    if (map._isLeafletImageMap) {
      removeImageTourLayers(map);
    } else {
      removePois({ map });
      removeNumericPois({ map });
      removePolygonPOIs({ map });
    }
  }

  // Building title labels: expanded map only (sync when expand/collapse or label setting changes).
  $: if (map && styleLoaded && layersAdded) {
    expanded;
    mapbox?.showBuildingLabels;
    mapbox?.buildings;
    syncBuildingLabelsForExpanded();
  }

  onDestroy(() => {
    destroyNumericHotspotPopup();
    if (map) {
      if (mapMoveHandler) map.off("move", mapMoveHandler);
      if (mapMoveEndHandler) map.off("moveend", mapMoveEndHandler);
      if (map._isLeafletImageMap) {
        removeImageTourLayers(map);
      } else {
        removePois({ map });
        removeNumericPois({ map });
        removePolygonPOIs({ map });
      }
    }
  });
</script>

{#if expanded && !isMobile && showLocationsMenu}
  <MapLocations {tour} {map} imageMap={!!map?._isLeafletImageMap} highlightItem={highlightItem} clearTrigger={mapMenuClearTrigger} externalSelectedKey={selectedItemMenuKey} onMeasurementActiveChange={setMapMeasurementActive} />
{/if}

<!-- Mobile: Locations slide-up sheet -->
{#if expanded && isMobile && showLocationsMenu && mobileLocationsOpen}
  <div
    class="mobile-locations-backdrop"
    role="presentation"
    on:click={onCloseMobileLocations}
    transition:fade={{ duration: sheetDuration }}
  />
  <div
    class="mobile-locations-sheet"
    class:mobile-locations-sheet-viewport={expanded && isMobile && mobileLocationsOpen}
    style={expanded && isMobile && mobileLocationsOpen ? `top: ${mobileSheetTop}px; height: ${mobileSheetHeight}px; bottom: auto;` : ''}
    role="dialog"
    aria-label="Locations"
    aria-modal="true"
    on:wheel|stopPropagation
    on:keydown={(e) => e.key === 'Escape' && onCloseMobileLocations()}
    in:fly={{ y: 80, duration: sheetDuration, easing: (t) => t * (2 - t) }}
    out:fly={{ y: 80, duration: sheetDuration * 0.9, easing: (t) => t * (2 - t) }}
  >
    <div class="mobile-locations-sheet-header">
      <h2 class="mobile-locations-sheet-title">Locations</h2>
      <button
        type="button"
        class="mobile-locations-sheet-close"
        aria-label="Close locations"
        on:click={onCloseMobileLocations}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>
      </button>
    </div>
    <div class="mobile-locations-sheet-content">
      <MapLocations {tour} {map} imageMap={!!map?._isLeafletImageMap} highlightItem={highlightItem} clearTrigger={mapMenuClearTrigger} externalSelectedKey={selectedItemMenuKey} onItemSelect={onCloseMobileLocations} focusFilterOnOpen={focusLocationsFilterOnOpen} onFocusFilterDone={onFocusLocationsFilterDone} filterAtBottom={true} onMeasurementActiveChange={setMapMeasurementActive} />
    </div>
  </div>
{/if}

<!-- Single floating label: selection (priority) or hover tooltip; selection has buttons below -->
{#key labelGeneration}
{#if ((selectedLabel && selectedLabelPixel && !showHotspotPopup) || (tooltipText && !isMobile && !selectedLabel))}
  {@const isSelection = !!(selectedLabel && selectedLabelPixel)}
  {@const useMobileInlineGalleryLayout = isSelection && isMobile && showInlineGallery}
  {@const useMobileCenterX = isSelection && isMobile && !useMobileInlineGalleryLayout}
  {@const x = isSelection ? selectedLabelPixel.x : tooltipX}
  {@const labelOffsetY = isSelection ? FLOATING_LABEL_OFFSET_Y : tooltipOffsetY}
  {@const anchorY = (isSelection ? selectedLabelPixel.y : tooltipY) - labelOffsetY}
  {@const useTopAnchor = isSelection && selectionStackHeight != null && !isNumericSelection}
  {@const y = useTopAnchor ? anchorY - selectionStackHeight : anchorY}
  {@const text = isSelection ? selectedLabel.text : tooltipText}
  {@const directionsLngLat =
    isSelection && selectedItem?.data
      ? getDirectionsCoordsForPopup(selectedItem.data, tour?.mapbox?.mapDisplayMode)
      : null}
  {@const directionsUrls = directionsLngLat ? getDirectionsUrls(directionsLngLat) : null}
  {@const showActions = isSelection && (directionsUrls || linkedScenes.length > 0)}
  {@const actionButtonCount = (directionsUrls ? 1 : 0) + (linkedScenes.length > 0 ? 1 : 0)}
  {@const compactActionsLayout = isSelection && !hasExpandableLabelContent && actionButtonCount === 1}
  {@const showLabelInteractions = isSelection && (showActions || hasExpandableLabelContent)}
  <div
    bind:this={floatingLabelEl}
    class="map-floating-label-wrapper"
    class:map-floating-label-with-actions={showLabelInteractions}
    class:map-floating-label-compact-actions={showLabelInteractions && compactActionsLayout}
    class:map-floating-label-is-selection={isSelection}
    class:map-floating-label-top-anchored={useTopAnchor}
    class:map-floating-label-mobile-inline-fixed={useMobileInlineGalleryLayout}
    class:map-floating-label-mobile-center-x={useMobileCenterX}
    style={useMobileInlineGalleryLayout
      ? null
      : useMobileCenterX
        ? `top: ${y}px;`
        : `left: ${x}px; top: ${y}px;`}
    role="status"
    aria-live="polite"
    in:fade={{ duration: 220 }}
    out:fade={{ duration: 180 }}
    on:outrostart={() => { floatingLabelExiting = true; }}
    on:outroend={() => { floatingLabelExiting = false; }}
    on:wheel|stopPropagation
  >
    <div
      class="map-floating-label-inner"
      class:map-floating-label-inner-interactive={showLabelInteractions && !floatingLabelExiting}
    >
    {#if isSelection}
      {#key selectedItemMenuKey}
        {#if isNumericSelection && numericHasExpandableBlock}
          <div
            class="map-floating-label-main"
            class:map-floating-label-main-scroll={useMobileInlineGalleryLayout}
          >
          {#if showInlineGallery}
            <div class="map-floating-label-inline-gallery-outside">
              <MapFloatingInlineGallery
                images={selectedGalleryImages}
              />
            </div>
          {/if}
          <div class="map-floating-label-block expanded">
            <div class="map-floating-label map-floating-label-in-block map-floating-label-title-row">
              <span class="map-floating-label-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              </span>
              <span class="map-floating-label-text">{text}</span>
              {#if showIconGallery}
                <button
                  type="button"
                  class="map-floating-label-gallery-btn"
                  on:click|stopPropagation={openMapGallery}
                  aria-label="Open gallery"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    width="20"
                    height="20"
                    viewBox="0 0 14 14"
                    class="map-floating-label-gallery-icon"
                  >
                    <path
                      fill="currentColor"
                      fill-rule="evenodd"
                      d="M2.484.445C3.46.35 4.504.25 5.577.25s2.117.1 3.093.195l.093.009a2.16 2.16 0 0 1 1.932 1.86c.103.772.208 1.609.208 2.472v.028l.613.058l.094.01a2.16 2.16 0 0 1 1.93 1.858c.104.773.209 1.61.209 2.473s-.105 1.7-.209 2.473a2.16 2.16 0 0 1-1.931 1.86l-.094.008c-.976.094-2.02.195-3.093.195s-2.117-.1-3.093-.195l-.093-.009a2.16 2.16 0 0 1-1.931-1.86c-.104-.772-.21-1.609-.21-2.472v-.028l-.612-.058l-.094-.01A2.16 2.16 0 0 1 .46 7.26C.355 6.486.25 5.65.25 4.786s.105-1.7.209-2.473A2.16 2.16 0 0 1 2.39.453zm2.873 5.68l.072-.006c.99-.096 1.982-.192 2.994-.192s2.004.096 2.994.192l.073.007c.416.04.757.367.812.78c.104.775.198 1.537.198 2.308s-.094 1.533-.198 2.307a.91.91 0 0 1-.812.78l-.073.008q-.45.044-.9.084c-.392-.707-.92-1.511-1.535-2.184c-.721-.789-1.657-1.504-2.706-1.504c-.701 0-1.353.32-1.926.77l-.003-.261c0-.771.094-1.533.197-2.307a.91.91 0 0 1 .813-.781m3.874.977c.247-.247.594-.36.993-.36s.746.113.993.36c.246.247.36.593.36.992c0 .4-.114.746-.36.993c-.248.247-.594.36-.993.36s-.746-.113-.993-.36s-.36-.594-.36-.993s.113-.745.36-.992"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
              {/if}
            </div>
            {#if hasLabelContent}
              <div class="map-floating-label-content">
                <div class="map-floating-label-content-inner">{@html labelContent}</div>
              </div>
            {/if}
          </div>
          </div>
        {:else if hasLabelContent}
          <div class="map-floating-label-block" class:expanded={contentExpanded}>
            <button
              type="button"
              class="map-floating-label map-floating-label-in-block"
              on:click|stopPropagation={toggleContentExpanded}
              aria-label={contentExpanded ? "Collapse description" : "Expand description"}
              aria-expanded={contentExpanded}
            >
              <span class="map-floating-label-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              </span>
              <span class="map-floating-label-text">{text}</span>
              <span class="map-floating-label-chevron" aria-hidden="true">
                <span class="map-label-chevron-icon" class:rotated={contentExpanded}>
                  <Icon icon="material-symbols:expand-more" width="20" height="20" />
                </span>
              </span>
            </button>
            {#if contentExpanded}
              <div class="map-floating-label-content" in:slide={{ duration: 180 }}>
                <div class="map-floating-label-content-inner">{@html labelContent}</div>
              </div>
            {/if}
          </div>
        {:else}
          <div class="map-floating-label">
            <span class="map-floating-label-icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            </span>
            <span class="map-floating-label-text">{text}</span>
          </div>
        {/if}
      {/key}
    {:else}
      <div class="map-floating-label">
        <span class="map-floating-label-icon" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
        </span>
        <span class="map-floating-label-text">{text}</span>
      </div>
    {/if}
    {#if showActions}
      <div
        class="map-floating-label-actions"
        in:fly={{ y: 10, duration: labelOpenDuration, opacity: 0, easing: labelEaseOut }}
      >
        <!-- Get Directions dropdown (leftmost) -->
        {#if directionsUrls}
          <div class="map-dropdown-wrapper">
            <button
              type="button"
              class="map-floating-label-btn"
              on:click|stopPropagation={toggleDirectionsDropdown}
              aria-label="Get directions"
              aria-expanded={directionsDropdownOpen}
              aria-haspopup="menu"
            >
              <Icon icon="material-symbols:directions-outline" width="1em" height="1em" />
              <span>Get Directions</span>
              <span class="map-label-chevron-icon dropdown-chevron" class:rotated={directionsDropdownOpen}>
                <Icon icon="material-symbols:expand-more" width="1em" height="1em" />
              </span>
            </button>
            {#if directionsDropdownOpen}
              <div class="map-dropdown-menu" role="menu" in:fly={{ y: 6, duration: dropdownDuration }} out:fly={{ y: 6, duration: dropdownDuration * 0.8 }}>
                <button
                  type="button"
                  class="map-dropdown-option map-dropdown-option-with-icon"
                  role="menuitem"
                  on:click|stopPropagation={() => openDirections(directionsUrls.google)}
                  aria-label="Open in Google Maps"
                >
                  <Icon icon="simple-icons:googlemaps" width="1em" height="1em" class="map-app-icon" />
                  <span>Google Maps</span>
                </button>
                <button
                  type="button"
                  class="map-dropdown-option map-dropdown-option-with-icon"
                  role="menuitem"
                  on:click|stopPropagation={() => openDirections(directionsUrls.apple)}
                  aria-label="Open in Apple Maps"
                >
                  <Icon icon="simple-icons:apple" width="1em" height="1em" class="map-app-icon" />
                  <span>Apple Maps</span>
                </button>
                <button
                  type="button"
                  class="map-dropdown-option map-dropdown-option-with-icon"
                  role="menuitem"
                  on:click|stopPropagation={() => openDirections(directionsUrls.waze)}
                  aria-label="Open in Waze"
                >
                  <Icon icon="simple-icons:waze" width="1em" height="1em" class="map-app-icon" />
                  <span>Waze</span>
                </button>
              </div>
            {/if}
          </div>
        {/if}
        <!-- Open 360(s) dropdown: always show dropdown so scene title(s) are visible -->
        {#if linkedScenes.length > 0}
          <div class="map-dropdown-wrapper">
            <button
              type="button"
              class="map-floating-label-btn"
              on:click|stopPropagation={toggle360Dropdown}
              aria-label={linkedScenes.length === 1 ? `Open 360: ${sceneTitleForDisplay(linkedScenes[0].title)}` : "Open 360s"}
              aria-expanded={open360DropdownOpen}
              aria-haspopup="menu"
            >
              <Icon icon="material-symbols:360" width="1em" height="1em" />
              <span>{linkedScenes.length === 1 ? "Open 360" : "Open 360s"}</span>
              <span class="map-label-chevron-icon dropdown-chevron" class:rotated={open360DropdownOpen}>
                <Icon icon="material-symbols:expand-more" width="1em" height="1em" />
              </span>
            </button>
          </div>
        {/if}
        <!-- Open 360(s) dropdown: full-width below actions row; absolute so it does not shift the label -->
        {#if linkedScenes.length > 0 && open360DropdownOpen}
          <div class="map-open360-dropdown-container" in:fly={{ y: 6, duration: dropdownDuration }} out:fly={{ y: 6, duration: dropdownDuration * 0.8 }}>
            <div class="map-dropdown-menu map-open360-dropdown-menu" role="menu">
              {#each linkedScenes as scene (scene.id)}
                <button
                  type="button"
                  class="map-dropdown-option map-dropdown-option-with-icon"
                  role="menuitem"
                  on:click|stopPropagation={() => openScene(scene.name)}
                  aria-label="Open {sceneTitleForDisplay(scene.title)} in virtual tour"
                >
                  <Icon icon="material-symbols:360" width="1em" height="1em" aria-hidden="true" />
                  <span class="map-dropdown-option-text">{sceneTitleForDisplay(scene.title)}</span>
                </button>
              {/each}
            </div>
          </div>
        {/if}
      </div>
    {/if}
    </div>
  </div>
{/if}
{/key}

<style>
  :root {
    --tooltip-background: #000;
    --tooltip-color: #fff;
    --tooltip-btn-bg: #fff;
    --tooltip-btn-color: #000;
    --tooltip-btn-hover: #e5e5e5;
    --tooltip-padding: 0.5rem;
    --tooltip-border-radius: 15px;
  }

  /* Wrapper: centered on cursor, anchored above (translate -50% -100%); shrink-wrap for hover, fixed width when actions open */
  .map-floating-label-wrapper {
    position: absolute;
    transform: translate(-50%, -100%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    pointer-events: none;
    z-index: 1000;
    width: 350px;
    /* Hover follows the cursor — no position transition (would lag). */
    transition: width 0.4s ease-out;
  }

  /* Selection: top-anchored so in-flow description expansion pushes buttons down, not the title up. */
  .map-floating-label-wrapper.map-floating-label-top-anchored {
    transform: translate(-50%, 0);
  }

  /* Mobile selection: X is always viewport center — not tied to POI pixel (map still pans POI under center). */
  .map-floating-label-wrapper.map-floating-label-mobile-center-x {
    left: 50%;
    width: min(350px, calc(100vw - 32px));
    max-width: calc(100vw - 32px);
    box-sizing: border-box;
  }

  /* Mobile inline gallery: viewport-centered card (decoupled from map anchor to avoid over-panning). */
  .map-floating-label-wrapper.map-floating-label-mobile-inline-fixed {
    position: fixed;
    left: 50%;
    top: calc(76px + (100dvh - 152px) / 2);
    transform: translate(-50%, -50%);
    width: 80vw;
    max-width: 80vw;
    overflow: visible;
    z-index: 1001;
  }

  .map-floating-label-wrapper.map-floating-label-mobile-inline-fixed .map-floating-label-inner {
    max-height: calc(100dvh - 152px);
    min-height: 0;
    overflow: visible;
  }

  .map-floating-label-main {
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 100%;
    box-sizing: border-box;
  }

  .map-floating-label-main-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  /* Action dropdowns open upward so they stay in the viewport (actions sit at card bottom). */
  .map-floating-label-wrapper.map-floating-label-mobile-inline-fixed .map-dropdown-menu {
    top: auto;
    bottom: 100%;
    margin-top: 0;
    margin-bottom: 4px;
  }

  .map-floating-label-wrapper.map-floating-label-mobile-inline-fixed .map-open360-dropdown-container {
    top: auto;
    bottom: 100%;
    margin-top: 0;
    margin-bottom: 4px;
  }

  .map-floating-label-wrapper.map-floating-label-mobile-inline-fixed.map-floating-label-is-selection {
    transition: width 0.4s ease-out;
  }

  /* Selected/open: ease position from hover point to map anchor and pop the label in. */
  .map-floating-label-wrapper.map-floating-label-is-selection {
    transition:
      left 0.42s cubic-bezier(0.22, 1, 0.36, 1),
      top 0.42s cubic-bezier(0.22, 1, 0.36, 1),
      width 0.42s ease-out;
  }

  .map-floating-label-wrapper.map-floating-label-is-selection .map-floating-label-inner {
    animation: map-label-open-inner 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  @keyframes map-label-open-inner {
    from {
      transform: translateY(-6px) scale(0.94);
      opacity: 0.82;
    }
    to {
      transform: translateY(0) scale(1);
      opacity: 1;
    }
  }

  .map-floating-label-wrapper:not(.map-floating-label-with-actions) {
    width: max-content;
    max-width: 220px;
  }

  .map-floating-label-wrapper.map-floating-label-compact-actions {
    width: max-content;
    max-width: min(350px, calc(100vw - 40px));
  }

  .map-floating-label-wrapper:not(.map-floating-label-with-actions) .map-floating-label-inner {
    width: auto;
  }

  .map-floating-label-compact-actions .map-floating-label-inner {
    width: max-content;
    max-width: 100%;
  }

  .map-floating-label-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    width: 100%;
    box-sizing: border-box;
  }

  .map-floating-label-inner .map-floating-label-actions {
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    .map-floating-label-wrapper:not(.map-floating-label-mobile-center-x):not(.map-floating-label-mobile-inline-fixed) {
      width: 80vw;
    }

    .map-floating-label-wrapper.map-floating-label-mobile-center-x.map-floating-label-compact-actions {
      width: max-content;
      max-width: calc(100vw - 32px);
    }

    .map-floating-label-wrapper.map-floating-label-compact-actions:not(.map-floating-label-mobile-center-x) {
      width: max-content;
      max-width: calc(100vw - 40px);
    }
  }

  .map-floating-label-inner-interactive {
    pointer-events: auto;
  }

  /* Inline gallery sits above the title/description pill, not inside it */
  .map-floating-label-inline-gallery-outside {
    width: 100%;
    box-sizing: border-box;
  }

  .map-floating-label-inline-gallery-outside :global(.map-floating-inline-gallery) {
    padding: 0;
  }

  /* When buttons are shown, label/block is at least as wide as the button row */
  .map-floating-label-with-actions .map-floating-label,
  .map-floating-label-with-actions .map-floating-label-block {
    min-width: 100%;
  }

  .map-floating-label-compact-actions .map-floating-label,
  .map-floating-label-compact-actions .map-floating-label-block {
    min-width: 100%;
    box-sizing: border-box;
  }

  .map-floating-label-compact-actions .map-floating-label-actions {
    width: auto;
    align-self: center;
  }

  .map-floating-label-compact-actions .map-floating-label-actions .map-dropdown-wrapper {
    flex: 0 0 auto;
    max-width: none;
  }

  .map-floating-label-compact-actions .map-floating-label-actions .map-floating-label-btn {
    width: auto;
  }

  /* Single block: label row + expandable content in flow (sits between label and buttons) */
  .map-floating-label-block {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    width: 100%;
    box-sizing: border-box;
    background: var(--tooltip-background, #000);
    color: var(--tooltip-color, #fff);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }

  .map-floating-label-block.expanded .map-floating-label-content {
    border-top: 1px solid rgba(255, 255, 255, 0.15);
  }

  /* Label row when inside block (no own bg/border); whole row is clickable */
  .map-floating-label.map-floating-label-in-block {
    border-radius: 0;
    border: none;
    box-shadow: none;
    cursor: pointer;
    color: inherit;
    width: 100%;
    box-sizing: border-box;
    text-align: left;
  }

  button.map-floating-label.map-floating-label-in-block {
    background: none;
    font-weight: 600;
  }

  button.map-floating-label.map-floating-label-in-block:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  .map-floating-label.map-floating-label-title-row {
    cursor: default;
    width: 100%;
    box-sizing: border-box;
    text-align: left;
    font-weight: 600;
  }

  .map-floating-label-title-row .map-floating-label-text {
    flex: 1;
    min-width: 0;
    white-space: normal;
  }

  .map-floating-label-gallery-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    padding: 4px;
    background: none;
    border: none;
    border-radius: 6px;
    color: inherit;
    cursor: pointer;
    flex-shrink: 0;
    opacity: 0.9;
  }

  .map-floating-label-gallery-icon {
    display: block;
  }

  .map-floating-label-gallery-btn:hover {
    background: rgba(255, 255, 255, 0.12);
    opacity: 1;
  }

  /* Top row: pill with icon + text (standalone tooltip or selection without content) */
  .map-floating-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px;
    line-height: 1.35;
    box-sizing: border-box;
    background: var(--tooltip-background, #000);
    color: var(--tooltip-color, #fff);
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    font-family: var(--font-heading), sans-serif;
    font-size: 18px;
    font-weight: 500;
    white-space: nowrap;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }

  /* Buttons row: centered under the anchor point */
  .map-floating-label-actions {
    position: relative;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    align-items: flex-start;
    gap: 6px;
    width: 100%;
  }

  .map-floating-label-actions .map-dropdown-wrapper {
    flex: 0 1 calc(50% - 3px);
    max-width: calc(50% - 3px);
    min-width: 0;
    box-sizing: border-box;
  }

  .map-floating-label-actions .map-floating-label-btn {
    width: 100%;
    justify-content: center;
  }

  .map-dropdown-wrapper {
    position: relative;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
  }

  .map-dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 2;
    margin-top: 4px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    min-width: 100%;
    background: var(--tooltip-btn-bg, #fff);
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    overflow: hidden;
  }

  .map-open360-dropdown-container {
    position: absolute;
    top: 100%;
    right: 0;
    width: max-content;
    max-width: 100%;
    margin-top: 4px;
    z-index: 2;
    box-sizing: border-box;
  }

  .map-open360-dropdown-container .map-dropdown-menu {
    position: static;
    margin-top: 0;
    width: max-content;
    max-width: 100%;
    min-width: 0;
  }

  .map-open360-dropdown-container .map-dropdown-option {
    width: auto;
    max-width: 100%;
  }

  .map-open360-dropdown-container .map-dropdown-option-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .map-dropdown-option {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    font-family: var(--font-body), sans-serif;
    font-size: 15px;
    font-weight: 500;
    color: var(--tooltip-btn-color, #000);
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
    width: 100%;
    min-width: 0;
  }

  .map-dropdown-option-text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .map-dropdown-option:hover {
    background: var(--tooltip-btn-hover, #e5e5e5);
  }

  .map-dropdown-option-with-icon {
    padding: 6px 10px;
  }

  .map-app-icon {
    color: #555;
    opacity: 0.9;
  }

  .dropdown-chevron {
    margin-left: 2px;
    opacity: 0.8;
  }

  .map-floating-label-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 4px 8px;
    font-family: var(--font-heading), sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: var(--tooltip-btn-color, #000);
    background: var(--tooltip-btn-bg, #fff);
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 999px;
    cursor: pointer;
    white-space: nowrap;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }

  .map-floating-label-btn:hover {
    background: var(--tooltip-btn-hover, #e5e5e5);
  }

  .map-floating-label-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    opacity: 0.95;
  }

  .map-floating-label-text {
    min-width: 110px;
    padding-right: 1em;
  }

  .map-floating-label-chevron {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    padding: 2px;
    background: none;
    border: none;
    border-radius: 4px;
    color: inherit;
    cursor: pointer;
    flex-shrink: 0;
  }


  .map-label-chevron-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    transition: transform 0.2s ease;
  }

  .map-label-chevron-icon :global(svg) {
    display: block;
  }

  .map-label-chevron-icon.rotated {
    transform: rotate(180deg);
  }

  .map-floating-label-content {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    max-height: 240px;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 10px 12px;
    box-sizing: border-box;
    background: var(--tooltip-background, #000);
    color: var(--tooltip-color, #fff);
    border-radius: 0 0 20px 20px;
    font-size: 14px;
    line-height: 1.5;
    scrollbar-width: thin;
    scrollbar-color: #fff rgba(255, 255, 255, 0);
  }

  .map-floating-label-content::-webkit-scrollbar {
    width: 8px;
  }

  .map-floating-label-content::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0);
    border-radius: 4px;
  }

  .map-floating-label-content::-webkit-scrollbar-thumb {
    background: #fff;
    border-radius: 4px;
  }

  .map-floating-label-content::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.85);
  }

  .map-floating-label-content-inner {
    font-family: var(--font-body), sans-serif;
  }

  .map-floating-label-content-inner :global(p) {
    margin: 0 0 0.5em 0;
  }

  .map-floating-label-content-inner :global(p:last-child) {
    margin-bottom: 0;
  }

  .map-floating-label-content-inner :global(a) {
    color: var(--tooltip-color, #fff);
  }

  /* Mobile Locations sheet */
  .mobile-locations-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1100;
  }

  .mobile-locations-sheet {
    position: fixed;
    left: 0;
    right: 0;
    /* End above the bottom icon bar (Locations / Virtual Tour / Search) to avoid z-index overlap */
    bottom: 72px;
    bottom: calc(72px + env(safe-area-inset-bottom, 0px));
    top: 20%;
    background: #fff;
    border-radius: 12px 12px 0 0;
    box-shadow: 0 -2px 20px rgba(0, 0, 0, 0.15);
    z-index: 1101;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .mobile-locations-sheet-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid #eee;
    flex-shrink: 0;
    background: #f5f5f5;
  }

  .mobile-locations-sheet-title {
    margin: 0;
    font-size: 1.2em;
    font-weight: 600;
    color: #333;
  }

  .mobile-locations-sheet-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    background: none;
    border: none;
    border-radius: 8px;
    color: #666;
    cursor: pointer;
  }

  .mobile-locations-sheet-close:hover {
    background: #eee;
    color: #333;
  }

  .mobile-locations-sheet-content {
    flex: 1;
    overflow: auto;
    position: relative;
    min-height: 0;
  }

  .mobile-locations-sheet-content :global(.maplocations-outer) {
    position: relative !important;
    top: 0 !important;
    right: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    flex: 1 !important;
    min-height: 0 !important;
    max-height: 100% !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 10px !important;
  }

  .mobile-locations-sheet-content :global(.maplocations-panel) {
    position: relative !important;
    top: 0 !important;
    right: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    flex: 1 !important;
    min-height: 0 !important;
    height: auto !important;
    max-height: none !important;
    border-radius: 0;
    box-shadow: none;
  }
</style>
